// Drop-in replacement for useReactToPrint that uses html2pdf
// Usage: Replace `import { useReactToPrint } from 'react-to-print'`
//        with `import { useReactToPrint } from '../../Shared/print/useHtml2PdfPrint'`
//
//API-compatible with react-to-print's useReactToPrint hook

import { useCallback } from 'react';
import { printDomAsPdfWithPopup } from './printUtils';

const PAPER_SIZES = {
  A4: { width: 210, height: 297 },
  A5: { width: 148, height: 210 },
  LETTER: { width: 216, height: 279 },
  LEGAL: { width: 216, height: 356 },
};

function parsePageStyle(pageStyle) {
  if (!pageStyle || typeof pageStyle !== 'string') return {};
  // rudimentary parse: look for 'size:' then read tokens until ';'
  const sizeIdx = pageStyle.indexOf('size');
  if (sizeIdx === -1) return {};
  const after = pageStyle.slice(sizeIdx);
  const match = after.match(/size\s*:\s*([^;]+);?/i);
  if (!match) return {};
  const tokens = match[1].trim().split(/\s+/);
  let orientation = 'portrait';
  let widthMM;
  let heightMM;

  // Check for keyword orientation
  const orientIdx = tokens.findIndex(t => /landscape|portrait/i.test(t));
  if (orientIdx !== -1) {
    orientation = tokens[orientIdx].toLowerCase();
    tokens.splice(orientIdx, 1);
  }

  if (tokens.length === 1) {
    const key = tokens[0].toUpperCase();
    const preset = PAPER_SIZES[key];
    if (preset) {
      widthMM = preset.width;
      heightMM = preset.height;
    }
  } else if (tokens.length >= 2) {
    const dim = tokens.slice(0, 2).map(t => {
      const m = t.match(/([0-9]+\.?[0-9]*)\s*mm/i);
      return m ? parseFloat(m[1]) : undefined;
    });
    if (dim[0] && dim[1]) {
      widthMM = dim[0];
      heightMM = dim[1];
    }
  }

  return { widthMM, heightMM, orientation };
}

export const useReactToPrint = (options = {}) => {
  const {
    content,
    onBeforeGetContent,
    onAfterPrint,
    pageStyle, // ignored - html2pdf handles this
    documentTitle, // ignored
    removeAfterPrint, // ignored
  } = options;

  return useCallback(async () => {
    try {
      // Call onBeforeGetContent if provided
      if (onBeforeGetContent) {
        await onBeforeGetContent();
      }

      // Get the content node
      const raw = typeof content === 'function' ? content() : content;
      // Support passing refs directly (ref.current)
      const node = raw && typeof raw === 'object' && 'current' in raw ? raw.current : raw;
      if (!node) {
        console.error('useHtml2PdfPrint: content is null or undefined');
        return;
      }
      
      console.log('useHtml2PdfPrint: Starting print for node:', node.className || node.tagName || '[unknown node]');

      // Wait for content to be ready (children exist and have actual content)
      let tries = 0;
      while (tries < 50) {
        const hasChildren = node.children && node.children.length > 0;
        const hasText = node.textContent && node.textContent.trim().length > 0;
        const hasHeight = node.offsetHeight > 0;
        
        if (hasChildren && (hasText || hasHeight)) break;
        await new Promise(r => setTimeout(r, 100));
        tries++;
      }
      
      // Extra delay to ensure layout is stable and all content is rendered
      await new Promise(r => setTimeout(r, 1000));
      
      const childCount = node.children ? node.children.length : 0;
      console.log('useHtml2PdfPrint: Content ready. Height:', node.offsetHeight, 'Children:', childCount);
      console.log('useHtml2PdfPrint: ScrollHeight:', node.scrollHeight, 'ClientHeight:', node.clientHeight);
      
      // Force reflow to ensure all content is measured
      node.style.height = 'auto';
      node.style.maxHeight = 'none';
      node.style.overflow = 'visible';
      node.style.position = 'static';
      void node.offsetHeight;
      
      // Wait a bit more for the layout to adjust
      await new Promise(r => setTimeout(r, 300));
      
      console.log('useHtml2PdfPrint: After height reset. Height:', node.offsetHeight, 'ScrollHeight:', node.scrollHeight);

      // Determine size/orientation from pageStyle or defaults
      let widthMM = 210;
      let heightMM = 297;
      let orientation = 'portrait';
      const parsed = parsePageStyle(pageStyle);
      if (parsed.widthMM) widthMM = parsed.widthMM;
      if (parsed.heightMM) heightMM = parsed.heightMM;
      if (parsed.orientation) orientation = parsed.orientation;

      // Special-case receipts and KOT: use thermal printable width (80->72mm, 58->48mm)
      if (
        node.classList?.contains('pos-print-container') ||
        node.classList?.contains('kot-print-container') ||
        node.querySelector?.('.pos-receipt-template-container') ||
        node.querySelector?.('.kot-container')
      ) {
        const settings = (() => { try { return JSON.parse(localStorage.getItem('PrinterSettings')||'{}'); } catch(_) { return {}; } })();
        const roll = settings?.receiptSize === '58' ? 58 : 80;
        widthMM = roll === 80 ? 72 : 48;
        heightMM = 297; // safe default; content usually shorter
        orientation = 'portrait';
      }

      // Special-case: Shift summary / tab receipts
      if (
        node.classList?.contains('print-container-shift-summary') ||
        node.querySelector?.('.print-container-shift-summary')
      ) {
        const settings = (() => { try { return JSON.parse(localStorage.getItem('PrinterSettings')||'{}'); } catch(_) { return {}; } })();
        const roll = settings?.receiptSize === '58' ? 58 : 80;
        widthMM = roll === 80 ? 72 : 48;
        heightMM = 297;
        orientation = 'portrait';
      }

      // Print using html2pdf with explicit size/orientation
      await printDomAsPdfWithPopup(node, { widthMM, heightMM, orientation });

      // Call onAfterPrint if provided
      if (onAfterPrint) {
        onAfterPrint();
      }
    } catch (error) {
      console.error('useHtml2PdfPrint error:', error);
    }
  }, [content, onBeforeGetContent, onAfterPrint]);
};

// Also export as default for flexibility
export default useReactToPrint;
